import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { UserPlus, Mail, Phone, Users, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import CreateParticipantForm from './CreateParticipantForm';

// ── Progression helpers ────────────────────────────────────────────────────────

const PROG_CONFIG = {
  green:  { label: 'God',                   className: 'bg-green-100 text-green-700' },
  yellow: { label: 'Kräver uppmärksamhet',  className: 'bg-yellow-100 text-yellow-700' },
  red:    { label: 'Risk',                  className: 'bg-red-100 text-red-700' },
};

// ── Status-badge helpers ───────────────────────────────────────────────────────

const STATUS_COLORS = {
  Aktiv: 'bg-green-100 text-green-700',
  Inväntar_beslut: 'bg-yellow-100 text-yellow-700',
  Pausad: 'bg-orange-100 text-orange-700',
  Avslutad: 'bg-gray-200 text-gray-600',
  active: 'bg-green-100 text-green-700',
};

function statusLabel(s) {
  if (s === 'Inväntar_beslut') return 'Inväntar beslut';
  return s || '—';
}

function participantDisplayName(p) {
  if (p.first_name || p.last_name) {
    return [p.first_name, p.last_name].filter(Boolean).join(' ');
  }
  return p.name || '—';
}

// ══════════════════════════════════════════════════════════════════════════════

const ParticipantsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [participants, setParticipants] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Inline-formulär
  const [showForm, setShowForm] = useState(false);

  // Tilldelningsdialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningParticipant, setAssigningParticipant] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [assignSaving, setAssignSaving] = useState(false);

  const isAdmin = user.role === 'super_admin' || user.role === 'customer_admin';

  const [progressionFilter, setProgressionFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/participants');
        setParticipants(res.data);
      } catch {
        toast.error('Kunde inte hämta deltagare');
      }
      if (isAdmin) {
        try {
          const res = await api.get('/users');
          setOrgUsers(res.data.filter((u) => u.role === 'employee'));
        } catch {
          // non-critical
        }
      }
      setLoading(false);
    };
    load();
  }, [isAdmin]);

  // ── Ny deltagare sparad ────────────────────────────────────────────────────
  const handleParticipantSaved = (newParticipant) => {
    setParticipants((prev) => [newParticipant, ...prev]);
    setShowForm(false);
  };

  // ── Tilldelning ────────────────────────────────────────────────────────────
  const openAssignDialog = (e, participant) => {
    e.stopPropagation();
    setAssigningParticipant(participant);
    setSelectedUserIds(participant.assigned_to || []);
    setAssignDialogOpen(true);
  };

  const toggleUser = (uid) => {
    setSelectedUserIds((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  const handleSaveAssignments = async () => {
    if (!assigningParticipant) return;
    setAssignSaving(true);
    try {
      const current = assigningParticipant.assigned_to || [];
      for (const uid of current) {
        if (!selectedUserIds.includes(uid)) {
          await api.delete(`/participants/${assigningParticipant.id}/assign/${uid}`);
        }
      }
      const toAdd = selectedUserIds.filter((uid) => !current.includes(uid));
      if (toAdd.length > 0) {
        await api.post(`/participants/${assigningParticipant.id}/assign`, { user_ids: toAdd });
      }
      setParticipants((prev) =>
        prev.map((p) =>
          p.id === assigningParticipant.id ? { ...p, assigned_to: selectedUserIds } : p
        )
      );
      toast.success('Tilldelning sparad!');
      setAssignDialogOpen(false);
    } catch {
      toast.error('Kunde inte spara tilldelning');
    } finally {
      setAssignSaving(false);
    }
  };

  const getAssignedNames = (participant) => {
    if (!participant.assigned_to?.length) return null;
    return participant.assigned_to
      .map((uid) => orgUsers.find((u) => u.id === uid)?.name || uid)
      .join(', ');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="participants-page">
        {/* ── Sidhuvud ────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-black tracking-tight">Deltagare</h1>
            <p className="text-muted-foreground mt-2">Hantera alla deltagare i systemet</p>
          </div>
          {isAdmin && !showForm && (
            <Button
              data-testid="create-participant-btn"
              className="rounded-full font-bold"
              onClick={() => setShowForm(true)}
            >
              <UserPlus className="mr-2" size={20} />
              Ny Deltagare
            </Button>
          )}
          {isAdmin && showForm && (
            <Button
              variant="ghost"
              data-testid="cancel-form-btn"
              className="rounded-full"
              onClick={() => setShowForm(false)}
            >
              <X size={16} className="mr-1" /> Avbryt
            </Button>
          )}
        </div>

        {/* ── Inline-formulär ─────────────────────────────────────────────── */}
        {showForm && (
          <div data-testid="inline-create-form">
            <CreateParticipantForm
              onSave={handleParticipantSaved}
              onCancel={() => setShowForm(false)}
            />
          </div>
        )}

        {/* ── Progressionsfilter ───────────────────────────────────────────── */}
        <div className="flex gap-2 flex-wrap">
          {[
            { key: 'all',    label: 'Alla' },
            { key: 'green',  label: 'God' },
            { key: 'yellow', label: 'Kräver uppmärksamhet' },
            { key: 'red',    label: 'Risk' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setProgressionFilter(f.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${
                progressionFilter === f.key
                  ? 'bg-primary text-[#333333]'
                  : 'bg-muted text-muted-foreground hover:bg-primary/20'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* ── Deltagarkort ─────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <Card className="col-span-full">
              <CardContent className="p-6">
                <p className="text-muted-foreground">Laddar deltagare...</p>
              </CardContent>
            </Card>
          ) : participants.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="p-12 text-center">
                <p className="text-muted-foreground text-lg">
                  Inga deltagare ännu. Skapa din första deltagare!
                </p>
              </CardContent>
            </Card>
          ) : (
            participants
              .filter(p =>
                progressionFilter === 'all' ||
                (p.progression && p.progression.color === progressionFilter)
              )
              .map((participant) => (
              <Card
                key={participant.id}
                data-testid={`participant-card-${participant.id}`}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/participants/${participant.id}`)}
              >
                <CardContent className="pt-5 pb-4 px-5 space-y-3">
                  <p className="font-heading font-bold text-lg leading-tight">
                    {participantDisplayName(participant)}
                  </p>

                  {participant.email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail size={14} className="text-muted-foreground shrink-0" />
                      <span className="truncate">{participant.email}</span>
                    </div>
                  )}
                  {participant.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone size={14} className="text-muted-foreground shrink-0" />
                      <span>{participant.phone}</span>
                    </div>
                  )}
                  {participant.entity_name && (
                    <p className="text-xs text-muted-foreground truncate">{participant.entity_name}</p>
                  )}
                  {isAdmin && participant.assigned_to?.length > 0 && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Users size={13} />
                      <span className="truncate">{getAssignedNames(participant)}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold ${
                          STATUS_COLORS[participant.status] || 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {statusLabel(participant.status)}
                      </span>
                      {participant.consent_given && (
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-600">
                          Samtycke
                        </span>
                      )}
                      {participant.progression && PROG_CONFIG[participant.progression.color] && (
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${PROG_CONFIG[participant.progression.color].className}`}>
                          {PROG_CONFIG[participant.progression.color].label}
                        </span>
                      )}
                    </div>
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs h-7 px-2"
                        onClick={(e) => openAssignDialog(e, participant)}
                        data-testid={`assign-btn-${participant.id}`}
                      >
                        <Users size={13} className="mr-1" />
                        Tilldela
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* ── Tilldelningsdialog ──────────────────────────────────────────────── */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-bold">
              Tilldela medarbetare — {assigningParticipant && participantDisplayName(assigningParticipant)}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-72 overflow-y-auto py-2">
            {orgUsers.length === 0 ? (
              <p className="text-sm text-muted-foreground">Inga medarbetare i organisationen.</p>
            ) : (
              orgUsers.map((u) => (
                <div key={u.id} className="flex items-center gap-3 p-2 rounded hover:bg-muted">
                  <Checkbox
                    id={`assign-${u.id}`}
                    checked={selectedUserIds.includes(u.id)}
                    onCheckedChange={() => toggleUser(u.id)}
                  />
                  <Label htmlFor={`assign-${u.id}`} className="cursor-pointer flex-1">
                    <span className="font-medium">{u.name}</span>
                    <span className="text-muted-foreground text-xs ml-2">{u.email}</span>
                  </Label>
                </div>
              ))
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setAssignDialogOpen(false)}>
              Avbryt
            </Button>
            <Button
              className="flex-1 rounded-full font-bold"
              onClick={handleSaveAssignments}
              disabled={assignSaving}
            >
              {assignSaving ? 'Sparar...' : 'Spara'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default ParticipantsPage;
